// Central content + imagery for Epeeka Studio

export const IMAGES = {
  heroPoster: "/videos/hero-villa.jpg",
  heroVideo: "/videos/hero-villa.mp4",
  heroVideoWebm: "/videos/hero-villa.webm",
  hiddenCosts: [
    "/images/hidden-cost-1.png",
    "/images/hidden-cost-2.png",
    "/images/hidden-cost-3.png",
  ],
  lifestyle: [
    "/images/lifestyle-1.png",
    "/images/lifestyle-2.png",
    "/images/lifestyle-3.png",
    "/images/lifestyle-4.png?v=2",
    "/images/lifestyle-5.png?v=2",
  ],
  caseStudies: [
    "/images/case-1.png",
    "/images/case-2.png",
    "/images/case-3.png?v=2",
  ],
  founder: "/images/founder.png",
  finalCta:
    "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
};

export const PROBLEM_QUESTIONS = [
  "Are you truly maximizing your plot?",
  "Will this villa still feel timeless in thirty years?",
  "Will your guests remember it?",
  "Will your children?",
  "Will it outperform every competing luxury villa?",
  "Or quietly become another expensive property?",
];

export const HIDDEN_COSTS = [
  {
    no: "01",
    title: "Designing only for aesthetics",
    body: "Beauty fades into the ordinary when it isn't engineered around how a family actually lives, gathers, and remembers. A stunning facade cannot compensate for a home that forgets its people.",
  },
  {
    no: "02",
    title: "Building a house instead of a legacy",
    body: "A house is finished the day construction ends. A legacy compounds — in memory, in meaning, and in market value — for generations after the last stone is laid.",
  },
  {
    no: "03",
    title: "Managing contractors instead of systems",
    body: "Owners are quietly drafted into a second job: chasing vendors, reconciling budgets, resolving delays. The true cost is measured in stress, dilution, and years you never get back.",
  },
];

export const PILLARS = ["Experience", "Legacy", "Freedom"];

export const FORMULA_CARDS = [
  {
    no: "01",
    title: "Experience Multiplier Effect",
    tagline: "How the villa makes people feel.",
    points: [
      "Guest psychology & arrival sequencing",
      "Hospitality flow between spaces",
      "Memorable, cinematic moments",
      "Higher bookings & premium rates",
      "Distinctive market positioning",
    ],
  },
  {
    no: "02",
    title: "Legacy Layer",
    tagline: "What the villa means across generations.",
    points: [
      "Family traditions, encoded in space",
      "Architecture with intention",
      "Landscape as living heritage",
      "A singular sense of identity",
      "Emotional storytelling throughout",
    ],
  },
  {
    no: "03",
    title: "Owner Freedom System",
    tagline: "How the villa gets built without the burden.",
    points: [
      "Orchestrated vendor coordination",
      "End-to-end project management",
      "Absolute budget clarity",
      "Stress-free, staged execution",
      "You stay the visionary, never the manager",
    ],
  },
];

export const LIFESTYLE_WORDS = [
  "Morning coffee",
  "Sunrise yoga",
  "Children playing",
  "Outdoor dining",
  "Fire pit evenings",
  "Infinity pool",
  "Family gatherings",
  "Wine nights",
  "Rain watching",
  "Slow living",
];

export const CASE_STUDIES = [
  {
    name: "The Coastal Meridian",
    location: "Alibaug, India",
    challenge: "A wide sea-facing plot with no anchor and no narrative.",
    vision: "A villa that dissolves the line between water and living.",
    transformation: "Reflection pools thread through every principal room.",
    experience: "Guests describe arriving as 'exhaling for the first time'.",
    result: "84% repeat-guest rate. Positioned in the top 3% of regional rentals.",
  },
  {
    name: "Grandmother's Courtyard",
    location: "Coorg, India",
    challenge: "Three generations, one hillside, competing definitions of home.",
    vision: "A modern estate built around a single, sacred courtyard.",
    transformation: "Every wing opens inward to a shared, ritual heart.",
    experience: "The family gathers here at dusk — unprompted, every evening.",
    result: "Valued 2.3x construction cost within eighteen months.",
  },
  {
    name: "The Quiet Ridge",
    location: "Nashik Valley, India",
    challenge: "A trophy plot at risk of becoming a trophy that sits empty.",
    vision: "A boutique-hospitality retreat that also earns.",
    transformation: "Six suites, one cellar, an infinity edge over the vines.",
    experience: "Booked out two seasons in advance by word of mouth alone.",
    result: "Self-sustaining asset. Legacy and yield, without compromise.",
  },
];

export const JOURNEY = [
  { no: "01", title: "Discovery", body: "We understand your life, not just your plot." },
  { no: "02", title: "Vision Mapping", body: "We translate feeling into architectural intent." },
  { no: "03", title: "Experience Design", body: "We choreograph how every moment unfolds." },
  { no: "04", title: "Legacy Layer", body: "We encode identity, ritual, and meaning." },
  { no: "05", title: "Execution", body: "We orchestrate systems, so you stay the visionary." },
  { no: "06", title: "Lifestyle Destination", body: "You inherit a living asset, not a house." },
];

export const WHO_FOR = [
  { title: "Homeowners", body: "Building the one home meant to last generations." },
  { title: "Second-home buyers", body: "Seeking a retreat that restores as much as it impresses." },
  { title: "NRIs", body: "Creating a rooted anchor back home, managed remotely." },
  { title: "Lifestyle investors", body: "Wanting assets that appreciate in value and in meaning." },
  { title: "Boutique hospitality investors", body: "Building destinations that command premium and loyalty." },
];

export const WHO_NOT_FOR = [
  "Not for template seekers.",
  "Not for bargain hunters.",
  "Not for generic architecture.",
  "Not for those unwilling to invest in something extraordinary.",
];

export const TESTIMONIALS = [
  {
    quote:
      "We asked for a beautiful home. Epeeka gave us a place our family now organizes its year around. That distinction is everything.",
    name: "Rohan & Aditi Mehra",
    role: "Private Residence, Bangalore",
  },
  {
    quote:
      "The freedom was the luxury. Decisions were framed, systems ran, and we simply experienced the vision coming to life.",
    name: "Dr. Kavita Nair",
    role: "Second Home, Coorg",
  },
  {
    quote:
      "It performs like a boutique hotel and feels like a heirloom. Guests remember it. Our accountant appreciates it.",
    name: "Vikram Shah",
    role: "Hospitality Investor, Nashik",
  },
];

export const CALENDLY_URL = "https://calendly.com/epeeka-ema/30min";
